import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCaptureLocation, upsertCaptureLocation } from "../../api/captures/captureLocation";
import { getCapturePurposes, upsertCapturePurposes } from "../../api/captures/capturePurposes";
import { getCaptureConstructionDetails, upsertCaptureConstructionDetails } from "../../api/captures/constructionDetails";
import { getCaptureHydrogeologicContext, upsertCaptureHydrogeologicContext } from "../../api/captures/hydrogeologicContext";
import { CaptureLocationRequest } from "../../types/model/captureLocation";
import { CapturePurposeRequest } from "../../types/model/capturePurpose";
import { ConstructionDetailsRequest } from "../../types/model/constructionDetails";
import { HydrogeologicContextRequest } from "../../types/model/hydrogeologicContext";

export const useCaptureGeneral = (captureIdStr?: string, crs: "wgs84" | "etrs89" = "etrs89") => {
  const queryClient = useQueryClient();
  const captureId = Number(captureIdStr);

  const locationQuery = useQuery({
    queryKey: ["capture", captureId, "location", crs],
    queryFn: () => getCaptureLocation(captureId, crs),
    enabled: !!captureId,
  });

  const purposesQuery = useQuery({
    queryKey: ["capture", captureId, "purposes"],
    queryFn: () => getCapturePurposes(captureId),
    enabled: !!captureId,
  });

  const constructionDetailsQuery = useQuery({
    queryKey: ["capture", captureId, "construction-details"],
    queryFn: () => getCaptureConstructionDetails(captureId),
    enabled: !!captureId,
  });

  const hydrogeologicContextQuery = useQuery({
    queryKey: ["capture", captureId, "hydrogeologic-context"],
    queryFn: () => getCaptureHydrogeologicContext(captureId),
    enabled: !!captureId,
  });

  const upsertLocationMutation = useMutation({
    mutationFn: (values: CaptureLocationRequest) => upsertCaptureLocation(captureId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capture", captureId, "location"] });
    },
  });

  const upsertPurposesMutation = useMutation({
    mutationFn: (values: CapturePurposeRequest) => upsertCapturePurposes(captureId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capture", captureId, "purposes"] });
    },
  });

  const upsertConstructionDetailsMutation = useMutation({
    mutationFn: (values: ConstructionDetailsRequest) => upsertCaptureConstructionDetails(captureId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capture", captureId, "construction-details"] });
    },
  });

  const upsertHydrogeologicContextMutation = useMutation({
    mutationFn: (values: HydrogeologicContextRequest) => upsertCaptureHydrogeologicContext(captureId, values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["capture", captureId, "hydrogeologic-context"] });
    },
  });

  return {
    locationData: locationQuery.data,
    purposesData: purposesQuery.data,
    constructionDetailsData: constructionDetailsQuery.data,
    hydrogeologicContextData: hydrogeologicContextQuery.data,
    isPending: locationQuery.isPending || purposesQuery.isPending || constructionDetailsQuery.isPending || hydrogeologicContextQuery.isPending,
    isError: locationQuery.isError || purposesQuery.isError || constructionDetailsQuery.isError || hydrogeologicContextQuery.isError,
    error: locationQuery.error || purposesQuery.error || constructionDetailsQuery.error || hydrogeologicContextQuery.error,
    upsertLocation: upsertLocationMutation.mutateAsync,
    upsertPurposes: upsertPurposesMutation.mutateAsync,
    upsertConstructionDetails: upsertConstructionDetailsMutation.mutateAsync,
    upsertHydrogeologicContext: upsertHydrogeologicContextMutation.mutateAsync,
    refetchLocation: locationQuery.refetch,
    refetchPurposes: purposesQuery.refetch,
    refetchConstructionDetails: constructionDetailsQuery.refetch,
    refetchHydrogeologicContext: hydrogeologicContextQuery.refetch,
  };
};
